// New admin page for viewing newsletter subscriber stats and downloading CSV

import React, { useEffect, useState } from "react";

import styled from "styled-components";

const Container = styled.main`
  max-width: 900px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Label = styled.div`
  color: var(--text);
  font-weight: 600;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Button = styled.button`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
`;

const Info = styled.p`
  color: var(--text-secondary);
`;

export const Admin: React.FC = () => {
  const [token, setToken] = useState<string>("");
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState<boolean>(false);

  const runQuery = async (query: string, variables?: any) => {
    const endpoint = `${window.location.protocol}//${window.location.hostname}:4001/graphql`;
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-admin-token": token,
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0].message || "Error");
    return json.data;
  };

  const fetchCount = async () => {
    try {
      setError(null);
      const data = await runQuery(`query { newsletterSubscribersCount }`);
      setCount(data.newsletterSubscribersCount);
    } catch (e: any) {
      setError(e.message);
      setCount(null);
    }
  };

  const downloadCsv = async () => {
    try {
      setError(null);
      const data = await runQuery(
        `query { newsletterSubscribers { id fullName email createdAt } }`
      );
      const rows = data.newsletterSubscribers as Array<any>;
      const header = ["id", "fullName", "email", "createdAt"];
      const csv = [
        header.join(","),
        ...rows.map((r) =>
          header.map((h) => JSON.stringify(r[h] ?? "")).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newsletter-subscribers.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message);
    }
  };

  useEffect(() => {
    // optional: fetch count automatically if token present from localStorage
    const saved = localStorage.getItem("adminToken") || "";
    if (saved) {
      setToken(saved);
      // don’t auto fetch to avoid accidental exposure
    }
  }, []);

  if (!unlocked) {
    return (
      <Container>
        <h1>Admin</h1>
        <Card>
          <Row>
            <Label>Enter access code</Label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Code"
            />
            <Button
              onClick={() => {
                if (token === "94Aida1_!") {
                  setUnlocked(true);
                  localStorage.setItem("adminToken", token);
                } else {
                  setError("Wrong code");
                }
              }}
            >
              Unlock
            </Button>
          </Row>
          {error && <Info style={{ color: "#dc2626" }}>{error}</Info>}
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h1>Admin</h1>
      <Card>
        <Row>
          <Label>Admin token</Label>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Enter admin token"
          />
          <Button
            onClick={() => {
              localStorage.setItem("adminToken", token);
              fetchCount();
            }}
          >
            Check count
          </Button>
          <Button onClick={downloadCsv}>Download CSV</Button>
        </Row>
        <div style={{ marginTop: 16 }}>
          {count !== null && <Info>Total subscribers: {count}</Info>}
          {error && <Info style={{ color: "#dc2626" }}>{error}</Info>}
        </div>
      </Card>
    </Container>
  );
};
